<template>
	<BaseModal :show="true" border-classes="border-t-6 border-tertiary-light" :prevent-auto-focus="true">
		<form class="flex flex-col bg-neutral max-h-[calc(100vh_-_8.125rem)] min-w-[40vw] max-w-[calc(100vw_-_8.125rem)]">
			<div class="flex flex-row align-middle">
				<p class="text-xl font-bold leading-7 text-heading">Maintenance Event</p>
				<img src="@/assets/images/icon/dismiss.svg" alt="Dismiss" title="Dismiss" class="ml-auto cursor-pointer" @click="cancel">
			</div>

			<div class="flex flex-col flex-grow overflow-y-auto overflow-x-hidden pt-4">
				<div class="border border-neutral rounded mb-4 px-4 py-2">
					<div class="flex w-full cursor-pointer" :class="[{ 'pb-2 -mb-4': showVariables }]" @click.prevent="showVariables = !showVariables">
						<span>Message variables</span>
						<div class="ml-auto">
							<img v-if="showVariables" src="@/assets/images/icon/minus.svg" alt="-" title="Collapse" >
							<img v-else src="@/assets/images/icon/plus.svg" alt="+" title="Expand">
						</div>
					</div>

					<PortalDataTable
						v-if="showVariables"
						:server-mode="false"
						:endpoints="endpoints"
						:row-data="variables"/>
				</div>

				<div class="flex space-x-4">
					<DatePicker id="start_at" label="Start date/time*" placeholder="Start date/time"
											:model-value="start_at" :errors="error" error-field="maintenance_event.start_at"
											class="w-full" custom-classes="placeholder-default h-11 cursor-pointer" :disabled="readOnly || eventToEdit?.started_at != null"
											@date-selected="startDateSelected"/>

					<DatePicker id="end_at" label="End date/time*" placeholder="End date/time"
											:model-value="end_at" :errors="error" error-field="maintenance_event.end_at"
											class="w-full" custom-classes="placeholder-default h-11 cursor-pointer" :disabled="readOnly || eventToEdit?.started_at != null" />
				</div>

				<InputText label="Pending Message*" type="textarea" v-model="pending_message" font-weight="font-normal" :disabled="readOnly" text-area-rows="2"
				           class="mt-1" :errors="error" error-field="maintenance_event.pending_message" />

				<InputText label="In-Progress Message*" type="textarea" v-model="in_progress_message" font-weight="font-normal" :disabled="readOnly" text-area-rows="2"
				           :errors="error" error-field="maintenance_event.in_progress_message" />

				<!-- Logout warning	-->
				<Popper class="w-fit" content="The amount of time to warn the user before they get logged out for the maintenance event. Optional." hover arrow>
					<CheckBox :class="[{ 'mb-4': !enableLogoutWarning }]" input-id="enable_warning" label="Enable logout warning"
										label-text-size-class="text-[16px]" :checked="false" v-model="enableLogoutWarning" :disabled="readOnly" />
				</Popper>

				<InputText v-if="enableLogoutWarning" label="Warning duration (minutes)*" type="number" placeholder="Warning duration (minutes)" input-height="h-11" class="mt-3"
									 font-weight="font-normal" v-model="warning_minutes" :errors="error" error-field="maintenance_event.warning_minutes" :disabled="readOnly"/>

				<InputText v-if="enableLogoutWarning" label="Warning Message*" type="textarea" v-model="warning_message" font-weight="font-normal" :disabled="readOnly" text-area-rows="2"
				           :errors="error" error-field="maintenance_event.warning_message" />
			</div>

			<div v-if="eventToEdit.finished_at" class="mt-1">
				<SubmitButton class="ml-auto" label="Close" :size="ButtonSize.Small" :loading="isLoading" @click="cancel" />
			</div>
			<div v-else class="flex flex-row w-full space-x-4">
				<SubmitButton class="ml-auto" label="Cancel" :type="ButtonType.Outline" :size="ButtonSize.Small" :disabled="readOnly" @click="cancel" />
				<SubmitButton label="Save" :size="ButtonSize.Small" :loading="isLoading" :disabled="readOnly" @click="saveClicked" />
			</div>
		</form>
	</BaseModal>

	<Teleport to="html">
		<MultiButtonModal
			v-if="showDiscardChangesModal"
			:show="true"
			icon="info"
			title="Discard Changes"
			message="Are you sure?"
			subMessage="Leaving now will discard any changes made."
			:buttons="[
									new ModalButtonData(
										'Cancel', () => { showDiscardChangesModal = false }, ButtonType.Outline, ButtonSize.Small
									),
									new ModalButtonData(
										'Yes', () => { showDiscardChangesModal = false; emit('dismiss') }, ButtonType.Default, ButtonSize.Small
									)
								]"
		/>
	</Teleport>
</template>

<script setup lang="js">
import InputText from '@/components/Forms/InputText.vue'
import { computed, onMounted, ref } from 'vue'
import SubmitButton from '@/components/Forms/Buttons/SubmitButton.vue'
import { ButtonSize, ButtonType } from '@/components/Forms/Buttons/SubmitButtonConstants.js'
import BaseModal from '@/components/Alerts/BaseModal.vue'
import { DatePickerObj } from '@/models/utils/InputUtils.js'
import DatePicker from '@/components/Forms/DatePicker.vue'
import { maintenanceApi } from '@/api/Support/MaintenanceApi.js'
import { ModalButtonData } from '@/components/Alerts/ModalButtonData.js'
import MultiButtonModal from '@/components/Alerts/MultiButtonModal.vue'
import { Format } from '@/models/utils/FormatUtils.js'
import CheckBox from '@/components/Forms/CheckBox.vue'
import Popper from 'vue3-popper'
import PortalDataTable from '@/components/DataTable/PortalDataTable.vue'
import { DataTableEndpointDefinition } from '@/models/datatable/DataTableEndpointDefinition.js'

const isLoading = ref(false)
const showDiscardChangesModal = ref(false)
const error = ref(null)
const readOnly = computed(() => {
	return isLoading.value || props.eventToEdit?.finished_at
})

const start_at = ref(new DatePickerObj('', {
	dateFormat: 'Y-m-d H:i:ss',
	altFormat: 'm/d/y h:i K',
	noCalendar: false,
	enableTime: true,
	time_24hr: false,
	minuteIncrement: 5,
}))
const end_at = ref(new DatePickerObj('', {
	dateFormat: 'Y-m-d H:i:ss',
	altFormat: 'm/d/y h:i K',
	noCalendar: false,
	enableTime: true,
	time_24hr: false,
	minuteIncrement: 5,
}))
const enableLogoutWarning = ref(true)
const defaultWarningMinutes = 5
const warning_minutes = ref(defaultWarningMinutes)
const defaultPendingMessage = 'The Portal will be unavailable due to scheduled maintenance for approximately {expected_duration} beginning at {expected_start_time} on {expected_start_date}. We apologize for any inconvenience this may cause.'
const defaultInProgressMessage = 'The Portal is undergoing scheduled maintenance and is expected to be unavailable until approximately {expected_end_time} on {expected_end_date}. We apologize for any inconvenience this may cause.'
const defaultWarningMessage = 'The Portal will be undergoing scheduled maintenance in approximately {time_until_start}. You will be automatically logged out at that time.'
const pending_message = ref(defaultPendingMessage)
const in_progress_message = ref(defaultInProgressMessage)
const warning_message = ref(defaultWarningMessage)
const showVariables = ref(false)

const columns = computed(() => {
	return [
		{ field: "name", title: "Name", sort: false},
		{ field: "description", title: "Description", sort: false },
		{ field: "example", title: "Example", sort: false },
	]
})

const endpoints = [
	new DataTableEndpointDefinition({
		enableSearch: false,
		initialSortColumn: '',
		columns: columns,
	})
]

const variables = [
	{	name: 'expected_duration', description: 'How long maintenance is expected to take', example: '5 minutes' },
	{	name: 'expected_start_time', description: 'Time when maintenance is expected to start', example: '12:00 PM MDT' },
	{	name: 'expected_start_date', description: 'Date when maintenance is expected to start', example: '07/22' },
	{	name: 'expected_end_time', description: 'Time when maintenance is expected to end', example: '01:00 PM MDT' },
	{	name: 'expected_end_date', description: 'Date when maintenance is expected to end', example: '07/22' },
	{	name: 'time_until_start', description: 'Time left until maintenance will begin', example: '4:55' },
]

const props = defineProps({
	eventToEdit: { type: Object, default: null },
})

const emit = defineEmits(['dismiss', 'eventCreated', 'eventUpdated'])

onMounted(() => {
	if (props.eventToEdit?.id) {
		start_at.value.date = props.eventToEdit.start_at
		end_at.value.date = props.eventToEdit.end_at
		warning_minutes.value = props.eventToEdit.warning_minutes
		pending_message.value = props.eventToEdit.pending_message
		in_progress_message.value = props.eventToEdit.in_progress_message
		warning_message.value = props.eventToEdit.warning_message
		enableLogoutWarning.value = (props.eventToEdit.warning_minutes !== null || props.eventToEdit.warning_message !== null)

		if (!enableLogoutWarning.value) {
			warning_minutes.value = defaultWarningMinutes
			warning_message.value = defaultWarningMessage
		}
	}
})

function cancel()
{
	if (changesMade())
	{
		showDiscardChangesModal.value = true
	}
	else
	{
		emit('dismiss')
	}
}

function changesMade()
{
	let event = props.eventToEdit

	if (event?.id) {
		const enableWarningChanged = enableLogoutWarning.value !== (props.eventToEdit.warning_minutes != null || props.eventToEdit.warning_message != null)
		// Did the checkbox change?
		// Or did warning minutes or message change (checkbox must be checked in this case)?
		const warningChanged = enableWarningChanged ||
			(
				enableLogoutWarning.value === true &&
				(event?.warning_minutes !== Number(warning_minutes.value) ||
					event?.warning_message !== warning_message.value
				)
			)

		return (event?.start_at !== start_at.value.getDate() ||
			event?.end_at !== end_at.value.getDate() ||
			event?.pending_message !== pending_message.value ||
			event?.in_progress_message !== in_progress_message.value ||
			warningChanged)
	}
	else {
		return start_at.value.getDate() ||
			end_at.value.getDate() ||
			Number(warning_minutes.value) !== defaultWarningMinutes ||
			pending_message.value !== defaultPendingMessage ||
			warning_message.value !== defaultWarningMessage ||
			in_progress_message.value !== defaultInProgressMessage ||
			enableLogoutWarning.value
	}
}

function saveClicked()
{
	if (!changesMade())
	{
		cancel()
		return
	}

	error.value = null
	isLoading.value = true

	if (props.eventToEdit?.id)
	{
		updateEvent(props.eventToEdit.id)
	}
	else
	{
		createEvent()
	}
}

function updateEvent(eventId) {
	maintenanceApi.updateMaintenanceEvent({
		'id': eventId,
		'start_at': start_at.value.getDate() ? Format.dateToUTC(new Date(start_at.value.getDate())) : null,
		'end_at': end_at.value.getDate() ? Format.dateToUTC(new Date(end_at.value.getDate())) : null,
		'warning_minutes': enableLogoutWarning.value ? Number(warning_minutes.value) : null,
		'pending_message': pending_message.value,
		'in_progress_message': in_progress_message.value,
		'warning_message': enableLogoutWarning.value ? warning_message.value : null,
	}, () => {
		isLoading.value = false
		emit('eventUpdated')
	}, (e) => {
		isLoading.value = false
		error.value = e.showErrorBanner()
	})
}

function createEvent() {
	maintenanceApi.createMaintenanceEvent({
		'start_at': start_at.value.getDate() ? Format.dateToUTC(new Date(start_at.value.getDate())) : null,
		'end_at': end_at.value.getDate() ? Format.dateToUTC(new Date(end_at.value.getDate())) : null,
		'warning_minutes': enableLogoutWarning.value ? Number(warning_minutes.value) : null,
		'pending_message': pending_message.value,
		'in_progress_message': in_progress_message.value,
		'warning_message': enableLogoutWarning.value ? warning_message.value : null,
	}, () => {
		isLoading.value = false
		emit('eventCreated')
	}, (e) => {
		isLoading.value = false
		error.value = e.showErrorBanner()
	})
}

/**
 * @param {Array<Date>} dates - Dates is an array because the picker supports multi-select
 */
function startDateSelected(dates) {
	const date = dates[0]

	date.setTime(date.getTime() + (1000 * 60 * 60)) // Add one hour
	end_at.value.date = date
}

</script>